/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense } from 'react';
import { Await, redirect, useAsyncError } from 'react-router';
import type { Route } from './+types/_app._index';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { fetchCarouselProducts } from '@/components/product-carousel/loaders';
import { fetchCategories } from '@/lib/api/categories.server';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { siteContext, type SiteContext } from '@salesforce/storefront-next-runtime/site-context';
import { Region } from '@/components/region';
import PopularCategories from '@/components/home/popular-categories';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';

import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { getLogger } from '@/lib/logger.server';

import hero01 from '/images/hero-01.webp';
import maharTharOg from '/images/mahindra-thar-og.jpg';
import hero02 from '/images/hero-02.webp';
import hero03 from '/images/hero-03.webp';
import hero04 from '/images/hero-04.webp';
import HeroCarousel, { HeroCarouselSkeleton, type HeroSlide } from '@/components/hero-carousel';
import { ProductCarouselSkeleton } from '@/components/product-carousel';
import { ProductCarouselWithData } from '@/components/product-carousel/carousel';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';

@PageType({
    name: 'Home Page',
    description: 'Main landing page with hero carousel, featured products, and help sections',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'headerbanner',
        name: 'Header Banner Region',
        description: 'Region for promotional banners and hero content',
        maxComponents: 3,
    },
    {
        id: 'main',
        name: 'Main Content Region',
        description: 'Region for main content',
        maxComponents: 10,
    },
])
export class HomePageMetadata {
    @AttributeDefinition({
        id: 'hideheaderFooter',
        name: 'Hide Header & Footer',
        description: 'When true, only the logo will be visible in the header and the footer will be hidden.',
        type: 'boolean',
        defaultValue: false,
    })
    hideheaderFooter?: boolean;
}

function FeaturedProductsError() {
    const error = useAsyncError() as NormalizedApiError;
    const { t } = useTranslation('home');
    return (
        <div role="alert" className="py-8 text-center text-muted-foreground">
            <p>{t('featuredProducts.loadFailed')}</p>
            {import.meta.env.DEV && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status}</span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}

export type HomePageData = {
    page: PageWithComponentData | null | ReturnType<typeof fetchPageWithComponentData>;
    searchResult: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    categories: Promise<ShopperProducts.schemas['Category'][]>;
    wishlistInitialState: Promise<WishlistInitialState>;
    pageUrl: string;
    ogImageUrl: string;
    hideheaderFooter: boolean;
};

/**
 * Server-side loader function that fetches home page data.
 * This function runs on the server during SSR and prepares data for the home page.
 * @returns Promise that resolves to an object containing search result promise
 */
export async function loader(args: Route.LoaderArgs): Promise<HomePageData> {
    const logger = getLogger(args.context);
    logger.debug('HomePage: loader starting');

    const config = getConfig(args.context);
    const requestUrl = new URL(args.request.url);

    // Bare "/" serves the homepage directly without redirecting to /:siteId/:localeId/
    // This avoids duplicate home page URLs and allows store.mahindra.com to load directly.

    // Redirect /default to valid fallback locale (e.g. /in/default -> /in/en-IN)
    if (args.params.localeId === 'default') {
        const siteAlias = config.siteAliasMap?.[config.defaultSiteId] ?? config.defaultSiteId;
        const fallbackLocale = config.i18n?.fallbackLng ?? 'en-IN';
        throw redirect(`/${siteAlias}/${fallbackLocale}/${requestUrl.search}`);
    }

    // Redirect when site alias was omitted (e.g. /en-IN/comingsoon -> /in/en-IN/comingsoon)
    const siteAlias = config.siteAliasMap?.[config.defaultSiteId] ?? config.defaultSiteId;
    const isSiteParamALocale = config.i18n?.supportedLngs?.includes(args.params.siteId as string);
    if (isSiteParamALocale) {
        const locale = args.params.siteId;
        const targetRoute = args.params.localeId ? `/${args.params.localeId}` : '';
        throw redirect(`/${siteAlias}/${locale}${targetRoute}${requestUrl.search}`);
    }

    const currency = (args.context.get(siteContext) as SiteContext).currency;
    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);

    const pagePromise = fetchPageWithComponentData(args, {
        pageId: 'homepage',
    });

    let page = null;
    try {
        page = await pagePromise;
    } catch {
        // Handled gracefully if page resolution fails
    }

    const pageData = page?.data as Record<string, unknown> | undefined;
    const pageCustom = (page as Record<string, unknown> | null | undefined)?.custom as
        | Record<string, unknown>
        | undefined;
    const hideheaderFooter = Boolean(
        pageData?.hideheaderFooter ??
            pageData?.hideHeaderFooter ??
            pageCustom?.hideheaderFooter ??
            pageCustom?.hideHeaderFooter ??
            (page as Record<string, unknown> | null | undefined)?.hideheaderFooter ??
            (page as Record<string, unknown> | null | undefined)?.hideHeaderFooter ??
            false
    );

    return {
        page: page ?? pagePromise,
        searchResult: hideheaderFooter
            ? Promise.resolve({
                  hits: [],
                  total: 0,
                  limit: 0,
                  offset: 0,
              } as unknown as ShopperSearch.schemas['ProductSearchResult'])
            : fetchCarouselProducts(args.context, {
                  categoryId: 'root',
                  limit: config.pages.home.featuredProductsCount,
                  currency: currency ?? undefined,
              }),
        categories: fetchCategories(args.context, 'root', 1),
        wishlistInitialState: fetchWishlistInitialState(args.context),
        pageUrl,
        ogImageUrl: new URL(maharTharOg, requestUrl.origin).href,
        hideheaderFooter,
    };
}

/**
 * Home page component that displays the home page content with granular Suspense boundaries.
 * Components within the page handle their own Suspense boundaries for progressive loading.
 * @returns JSX element representing the home page layout
 */
export default function HomePage({ loaderData }: { loaderData: HomePageData }) {
    const { t } = useTranslation('home');

    const heroSlides: HeroSlide[] = [
        {
            id: 'slide-1',
            title: t('hero.slide1.title'),
            subtitle: t('hero.slide1.subtitle'),
            imageUrl: hero01,
            imageAlt: t('hero.slide1.imageAlt'),
            ctaText: t('hero.slide1.ctaText'),
            ctaLink: '/category/root',
            overlayPosition: 'Middle Center',
            overlayAlignment: 'center',
        },
        {
            id: 'slide-2',
            title: t('hero.slide2.title'),
            subtitle: t('hero.slide2.subtitle'),
            imageUrl: hero02,
            imageAlt: t('hero.slide2.imageAlt'),
            ctaText: t('hero.slide2.ctaText'),
            ctaLink: '/category/root',
            overlayPosition: 'Middle Center',
            overlayAlignment: 'center',
        },
        {
            id: 'slide-3',
            title: t('hero.slide3.title'),
            subtitle: t('hero.slide3.subtitle'),
            imageUrl: hero03,
            imageAlt: t('hero.slide3.imageAlt'),
            ctaText: t('hero.slide3.ctaText'),
            ctaLink: '/category/root',
            overlayPosition: 'Middle Center',
            overlayAlignment: 'center',
        },
        {
            id: 'slide-4',
            title: t('hero.slide4.title'),
            subtitle: t('hero.slide4.subtitle'),
            imageUrl: hero04,
            imageAlt: t('hero.slide4.imageAlt'),
            ctaText: t('hero.slide4.ctaText'),
            ctaLink: '/category/root',
            overlayPosition: 'Middle Center',
            overlayAlignment: 'center',
        },
    ];

    return (
        <WishlistProvider initialState={loaderData.wishlistInitialState}>
            <div className={cn('pb-10 -mt-8 home-page', loaderData.hideheaderFooter && 'comingsoon-page')}>
                <SeoMeta
                    rawTitle
                    title={t('meta.title', {
                        defaultValue: 'Mahindra Accessories & Merchandise Store | Official Mahindra Online Shop',
                    })}
                    description={t('meta.description', {
                        defaultValue: 'Mahindra Official Store',
                    })}
                    openGraph={{
                        type: 'website',
                        url: loaderData.pageUrl,
                        image: loaderData.ogImageUrl,
                    }}
                />
                {/* Header Banner Region - Region component handles its own Suspense internally */}
                <div>
                    <Region
                        page={loaderData.page}
                        regionId="headerbanner"
                        fallbackElement={
                            <>
                                {/* Provide fallback skeletons for the above the fold content */}
                                <HeroCarouselSkeleton showDots={true} showNavigation={true} />
                                <ProductCarouselSkeleton title={t('featuredProducts.title')} />
                            </>
                        }
                        errorElement={
                            loaderData.hideheaderFooter ? null : (
                                <>
                                    <HeroCarousel
                                        slides={heroSlides}
                                        autoPlay={true}
                                        autoPlayInterval={6000}
                                        showNavigation={true}
                                        showDots={true}
                                    />

                                    {/* Featured Products */}
                                    <Suspense
                                        fallback={<ProductCarouselSkeleton title={t('featuredProducts.title')} />}>
                                        <Await
                                            resolve={loaderData.searchResult}
                                            errorElement={<FeaturedProductsError />}>
                                            {(searchResult) => (
                                                <ProductCarouselWithData
                                                    data={searchResult}
                                                    title={t('featuredProducts.title')}
                                                    shopAllUrl="/category/root"
                                                    shopAllText={t('featuredProducts.shopAll')}
                                                />
                                            )}
                                        </Await>
                                    </Suspense>
                                </>
                            )
                        }
                    />
                </div>

                {/* Main Region - Region component handles its own Suspense internally */}
                {/* Note: This region doesn't provide fallback skeletons right now as it's located below the fold */}
                <Region
                    page={loaderData.page}
                    regionId="main"
                    errorElement={
                        loaderData.hideheaderFooter ? null : (
                            <>
                                {/* Popular Categories - full-width section with its own gray bg and container */}
                                <PopularCategories categoriesPromise={loaderData.categories} />
                            </>
                        )
                    }
                />
            </div>
        </WishlistProvider>
    );
}
